// src/components/Logs/LogsView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../../store/useStore';
import { logsApi, appsApi } from '../../services/api';
import LogRow from './LogRow';
import { Search, RefreshCw, AppWindow, Cloud, Server, Leaf, Database, AlertTriangle, Activity } from 'lucide-react';

const SOURCES = ['ALL', 'GCP', 'AZURE', 'MONGODB', 'DATABASE'];
const LEVELS  = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const SOURCE_META = {
  GCP:      { color: '#4285f4', icon: Cloud,     label: 'Google Cloud' },
  AZURE:    { color: '#0078d4', icon: Server,     label: 'Microsoft Azure' },
  MONGODB:  { color: '#4ade80', icon: Leaf,       label: 'MongoDB' },
  DATABASE: { color: '#a78bfa', icon: Database,   label: 'Database' },
};

const LEVEL_COLORS = {
  ERROR: '#ef4444', CRITICAL: '#fca5a5', WARN: '#fbbf24', WARNING: '#fbbf24', INFO: '#60a5fa', DEBUG: '#64748b',
};

export default function LogsView() {
  const { logs, logFilter, setLogFilter, addLogs } = useStore();
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [activeTab, setActiveTab] = useState('stream');
  const [apps, setApps]         = useState([]);
  const bottomRef = useRef(null);

  async function fetchLogs() {
    setLoading(true);
    try {
      const data = await logsApi.list({
        source: logFilter.source !== 'ALL' ? logFilter.source : undefined,
        level:  logFilter.level  !== 'ALL' ? logFilter.level  : undefined,
        search: search || undefined,
        since: 60, limit: 200,
      });
      addLogs(data.logs || []);
    } finally { setLoading(false); }
  }

  async function loadApps() {
    try { setApps(await appsApi.list()); } catch {}
  }

  useEffect(() => { fetchLogs(); loadApps(); }, [logFilter.source, logFilter.level]);

  const filtered = logs.filter(l => {
    if (logFilter.source !== 'ALL' && l.source !== logFilter.source) return false;
    if (logFilter.level  !== 'ALL' && l.level  !== logFilter.level)  return false;
    if (search && !l.message.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).slice(0, 500);

  return (
    <div className="flex flex-col h-full">
      {/* Top toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 shrink-0"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <span className="text-sm font-semibold">📋 Log Explorer</span>

        <div className="flex gap-1 ml-4">
          {SOURCES.map(s => (
            <button key={s} onClick={() => setLogFilter({ source: s })}
              className="px-2 py-1 rounded text-xs font-medium transition-all"
              style={{ background: logFilter.source === s ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)', color: logFilter.source === s ? '#60a5fa' : 'var(--text-3)', border: `1px solid ${logFilter.source === s ? 'rgba(59,130,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

        <div className="flex gap-1">
          {LEVELS.map(lv => (
            <button key={lv} onClick={() => setLogFilter({ level: lv })}
              className="px-2 py-1 rounded text-xs font-medium transition-all"
              style={{ background: logFilter.level === lv ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)', color: logFilter.level === lv ? '#60a5fa' : 'var(--text-3)', border: `1px solid ${logFilter.level === lv ? 'rgba(59,130,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
              {lv}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1 rounded px-2"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
          <Search size={12} style={{ color: 'var(--text-3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages…"
            className="bg-transparent outline-none text-xs py-1 w-40" style={{ color: 'var(--text)' }} />
        </div>

        <button onClick={fetchLogs}
          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>

        <div className="flex items-center gap-1 text-xs" style={{ color: '#22c55e' }}>
          <span className="pulse-dot" />LIVE
        </div>
      </div>

      {/* Tab bar */}
      <div className="px-4 flex gap-4 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        {[
          { id: 'stream', label: '🔴 Stream', sub: `${filtered.length} entries` },
          { id: 'byapp',  label: '⚡ By Application', sub: `${apps.length} apps` },
          { id: 'byinfra',label: '🏗️ By Infrastructure', sub: `${SOURCES.slice(1).length} sources` },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 0', fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? '#60a5fa' : 'var(--text-2)',
              borderBottom: activeTab === t.id ? '2px solid #60a5fa' : '2px solid transparent',
              background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {t.label}
            <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 400 }}>{t.sub}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'stream' && <StreamView filtered={filtered} logs={logs} bottomRef={bottomRef} />}
      {activeTab === 'byapp'  && <ByAppView apps={apps} logs={filtered} />}
      {activeTab === 'byinfra'&& <ByInfraView logs={filtered} />}
    </div>
  );
}

/* ── Stream Tab ────────────────────────────────────────────────────────────── */
function StreamView({ filtered, logs, bottomRef }) {
  return (
    <>
      <div className="px-4 py-1.5 text-xs mono shrink-0"
        style={{ background: 'var(--bg-secondary)', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
        Showing {filtered.length} entries · {logs.length} in memory
      </div>
      <div className="flex-1 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
        {filtered.map((log, i) => <LogRow key={`${log._id}-${i}`} log={log} />)}
        {filtered.length === 0 && (
          <div className="p-8 text-center mono text-xs" style={{ color: 'var(--text-3)' }}>No logs match the current filter</div>
        )}
        <div ref={bottomRef} />
      </div>
    </>
  );
}

/* ── By Application Tab ─────────────────────────────────────────────────────── */
function ByAppView({ apps, logs }) {
  const [selectedApp, setSelectedApp] = useState(null);

  // Group logs by service name; match to registered apps by servicePattern or service name
  const serviceGroups = {};
  logs.forEach(l => {
    const svc = l.service || 'unknown';
    if (!serviceGroups[svc]) serviceGroups[svc] = [];
    serviceGroups[svc].push(l);
  });

  // For each app, match logs by servicePattern
  const appGroups = apps.map(app => {
    const pattern = app.servicePattern || app.slug || app.name.toLowerCase().replace(/\s+/g, '-');
    const matchedLogs = logs.filter(l => {
      const svc = (l.service || '').toLowerCase();
      return svc === pattern || svc.includes(pattern) || pattern.includes(svc);
    });
    return { app, logs: matchedLogs };
  });

  // Also add ungrouped logs (services not matched to any registered app)
  const matchedServices = new Set(appGroups.flatMap(g => g.logs.map(l => l.service)));
  const ungroupedLogs = logs.filter(l => !matchedServices.has(l.service));

  // Group ungrouped logs by service
  const ungroupedByService = {};
  ungroupedLogs.forEach(l => {
    const svc = l.service || 'unknown';
    if (!ungroupedByService[svc]) ungroupedByService[svc] = [];
    ungroupedByService[svc].push(l);
  });

  const selectedLogs = selectedApp
    ? (appGroups.find(g => g.app._id === selectedApp)?.logs || ungroupedByService[selectedApp] || [])
    : [];

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* App list */}
      <div className="shrink-0 overflow-y-auto p-4" style={{ width: 320, borderRight: '1px solid var(--border)' }}>
        <div className="text-xs font-semibold mb-3 px-1" style={{ color: 'var(--text-3)', letterSpacing: '1px', textTransform: 'uppercase' }}>
          Registered Apps ({appGroups.length})
        </div>
        {appGroups.length === 0 && (
          <div className="text-xs p-3 rounded-lg mb-3" style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', color: '#fbbf24' }}>
            No applications registered. Add them in Applications → to enable app-wise log routing.
          </div>
        )}
        {appGroups.map(({ app, logs: appLogs }) => {
          const errCount = appLogs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
          const isSelected = selectedApp === app._id;
          return (
            <button key={app._id} onClick={() => setSelectedApp(isSelected ? null : app._id)}
              className="w-full text-left rounded-lg p-3 mb-2"
              style={{ background: isSelected ? 'rgba(59,130,246,.15)' : 'var(--bg-secondary)', border: `1px solid ${isSelected ? 'rgba(59,130,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{app.name}</span>
                {errCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-bold" style={{ color: '#f87171' }}>
                    <AlertTriangle size={10} /> {errCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-3)' }}>
                <span><Activity size={10} style={{ display: 'inline', marginRight: 3 }} />{appLogs.length} logs</span>
                <span style={{ color: appLogs.length === 0 ? '#64748b' : errCount > 0 ? '#f87171' : '#22c55e', fontWeight: 600 }}>
                  {appLogs.length === 0 ? 'no data' : errCount > 0 ? `${((errCount / appLogs.length) * 100).toFixed(0)}% errors` : 'healthy'}
                </span>
              </div>
            </button>
          );
        })}

        {Object.keys(ungroupedByService).length > 0 && (
          <>
            <div className="text-xs font-semibold mb-2 mt-3 px-1" style={{ color: 'var(--text-3)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Unregistered Services ({Object.keys(ungroupedByService).length})
            </div>
            {Object.entries(ungroupedByService).map(([svc, svcLogs]) => {
              const errCount = svcLogs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
              const isSelected = selectedApp === svc;
              return (
                <button key={svc} onClick={() => setSelectedApp(isSelected ? null : svc)}
                  className="w-full text-left rounded-lg p-3 mb-2"
                  style={{ background: isSelected ? 'rgba(59,130,246,.15)' : 'var(--bg-tertiary)', border: `1px solid ${isSelected ? 'rgba(59,130,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium mono" style={{ color: 'var(--text-2)' }}>{svc}</span>
                    {errCount > 0 && <span className="text-xs font-bold" style={{ color: '#f87171' }}>{errCount} err</span>}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>{svcLogs.length} logs</div>
                </button>
              );
            })}
          </>
        )}
      </div>

      {/* Log stream for selected app */}
      <div className="flex-1 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
        {!selectedApp ? (
          <div className="flex flex-col items-center justify-center h-full gap-3" style={{ color: 'var(--text-3)' }}>
            <AppWindow size={32} />
            <div className="text-sm">Select an application to view its logs</div>
          </div>
        ) : selectedLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3" style={{ color: 'var(--text-3)' }}>
            <Activity size={32} />
            <div className="text-sm">No logs found for this application in the current time window</div>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 text-xs mono shrink-0 sticky top-0" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', color: 'var(--text-3)' }}>
              {selectedLogs.length} log entries
            </div>
            {selectedLogs.map((log, i) => <LogRow key={`${log._id}-${i}`} log={log} />)}
          </>
        )}
      </div>
    </div>
  );
}

/* ── By Infrastructure Tab ──────────────────────────────────────────────────── */
function ByInfraView({ logs }) {
  const [selectedSrc, setSelectedSrc] = useState(null);

  const sourceStats = SOURCES.slice(1).map(src => {
    const srcLogs = logs.filter(l => l.source === src);
    const errors  = srcLogs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
    const warns   = srcLogs.filter(l => l.level === 'WARN' || l.level === 'WARNING').length;
    const meta    = SOURCE_META[src];
    const errPct  = srcLogs.length > 0 ? ((errors / srcLogs.length) * 100).toFixed(1) : '0.0';
    // Group by service within this source
    const services = {};
    srcLogs.forEach(l => {
      const svc = l.service || 'default';
      if (!services[svc]) services[svc] = 0;
      services[svc]++;
    });
    return { src, srcLogs, errors, warns, errPct, meta, services };
  });

  const selectedData = sourceStats.find(s => s.src === selectedSrc);
  const selectedLogs = selectedData?.srcLogs || [];

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Source panels */}
      <div className="shrink-0 overflow-y-auto p-4" style={{ width: 320, borderRight: '1px solid var(--border)' }}>
        <div className="text-xs font-semibold mb-3 px-1" style={{ color: 'var(--text-3)', letterSpacing: '1px', textTransform: 'uppercase' }}>Infrastructure Sources</div>
        {sourceStats.map(({ src, srcLogs, errors, warns, errPct, meta }) => {
          const Icon = meta.icon;
          const isSelected = selectedSrc === src;
          const health = parseFloat(errPct);
          const healthColor = health > 10 ? '#ef4444' : health > 2 ? '#f59e0b' : '#22c55e';

          return (
            <button key={src} onClick={() => setSelectedSrc(isSelected ? null : src)}
              className="w-full text-left rounded-xl p-4 mb-3"
              style={{ background: isSelected ? `${meta.color}15` : 'var(--bg-secondary)', border: `1px solid ${isSelected ? meta.color + '60' : 'var(--border)'}`, cursor: 'pointer' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}30` }}>
                  <Icon size={15} style={{ color: meta.color }} />
                </div>
                <div>
                  <div className="font-semibold text-sm">{src}</div>
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>{meta.label}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-sm font-bold" style={{ color: healthColor }}>{errPct}%</div>
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>error rate</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded p-1.5" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="font-bold">{srcLogs.length}</div>
                  <div style={{ color: 'var(--text-3)' }}>total</div>
                </div>
                <div className="rounded p-1.5" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="font-bold" style={{ color: errors > 0 ? '#f87171' : 'var(--text)' }}>{errors}</div>
                  <div style={{ color: 'var(--text-3)' }}>errors</div>
                </div>
                <div className="rounded p-1.5" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="font-bold" style={{ color: warns > 0 ? '#fbbf24' : 'var(--text)' }}>{warns}</div>
                  <div style={{ color: 'var(--text-3)' }}>warns</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Log stream for selected source */}
      <div className="flex-1 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
        {!selectedSrc ? (
          <div className="flex flex-col items-center justify-center h-full gap-3" style={{ color: 'var(--text-3)' }}>
            <Cloud size={32} />
            <div className="text-sm">Select an infrastructure source to explore its logs</div>
          </div>
        ) : (
          <>
            {/* Source header */}
            <div className="px-4 py-3 sticky top-0 flex items-center gap-3"
              style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
              {(() => {
                const meta = SOURCE_META[selectedSrc];
                const Icon = meta.icon;
                return <>
                  <Icon size={15} style={{ color: meta.color }} />
                  <span className="font-semibold text-sm">{selectedSrc}</span>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>{selectedLogs.length} entries</span>
                  {/* Service breakdown */}
                  <div className="ml-auto flex gap-2 flex-wrap">
                    {Object.entries(selectedData?.services || {}).slice(0, 5).map(([svc, cnt]) => (
                      <span key={svc} className="text-xs px-2 py-0.5 rounded"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                        {svc} ({cnt})
                      </span>
                    ))}
                  </div>
                </>;
              })()}
            </div>
            {selectedLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-3" style={{ color: 'var(--text-3)' }}>
                <div className="text-sm">No logs from {selectedSrc} in the current time window</div>
              </div>
            ) : (
              selectedLogs.map((log, i) => <LogRow key={`${log._id}-${i}`} log={log} />)
            )}
          </>
        )}
      </div>
    </div>
  );
}
